import React, { useState, useEffect } from 'react';
import { Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBoxes, 
  faShoppingCart, 
  faSignOutAlt, faBook, faUsers, 
  faFileInvoice, 
  faFileAlt, 
  faCalculator,
  faTruckMoving,
  faWarehouse,
  faFileInvoiceDollar,
  faUserCog,
  faClipboardCheck
} from '@fortawesome/free-solid-svg-icons';
import { Outlet, useNavigate, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { getPendingApprovalsCount } from '../services/api';
import './Layout.css';

function Layout() {
  const { user, logout, hasPermission, permissions, loading } = useAuth();
  const { tenantConfig } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const hoverTimeoutRef = React.useRef(null);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // Always start with collapsed sidebar on mobile
      if (mobile) {
        setIsExpanded(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Reset sidebar state when navigating on mobile
  useEffect(() => {
    if (isMobile) {
      setIsExpanded(false);
    }
  }, [location.pathname, isMobile]);

  // Poll for pending approvals count if user has approve permission
  useEffect(() => {
    const fetchPendingCount = async () => {
      if (hasPermission('inventory', 'approve')) {
        const count = await getPendingApprovalsCount();
        setPendingApprovalsCount(count);
      }
    };

    // Fetch immediately
    fetchPendingCount();

    // Poll every 30 seconds
    const interval = setInterval(fetchPendingCount, 30000);

    // Listen for manual refresh requests
    const handleRefresh = () => {
      fetchPendingCount();
    };
    
    window.addEventListener('refreshPendingCount', handleRefresh);

    return () => {
      clearInterval(interval);
      window.removeEventListener('refreshPendingCount', handleRefresh);
    };
  }, [hasPermission, permissions, location.pathname]); // Refresh when navigating

  // Cleanup hover timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  if (!user) {
    // Don't redirect if still loading - wait for auth check to complete
    if (loading) {
      return null; // Show nothing while checking auth
    }
    return <Navigate to="/login" />;
  }

  const handleLogout = async () => {
    await logout();
    const tenantPrefix = params.tenantPrefix;
    if (tenantPrefix) {
      navigate(`/${tenantPrefix}/login`);
    } else {
      navigate('/login');
    }
  };

  // Helper function to generate tenant-aware links
  const getTenantLink = (path) => {
    const tenantPrefix = params.tenantPrefix;
    if (tenantPrefix) {
      return `/${tenantPrefix}${path}`;
    }
    return path;
  };

  // Navigation handler
  const handleNavigation = (path) => {
    navigate(getTenantLink(path));
    handleNavClick();
  };

  const isInventoryActive = location.pathname.includes('/inventory') || location.pathname.includes('/orders') || location.pathname.includes('/catalog');
  const isOrdersActive = location.pathname.includes('/orders');
  const isWarehouseReceiptsActive = location.pathname.includes('/warehouse-receipts');
  const isBOLsActive = location.pathname.includes('/bols');
  const isRateCalcActive = location.pathname.includes('/rate-calculator');
  const isInvoicesActive = location.pathname.includes('/invoices') && !location.pathname.includes('/profile');
  const isProfileActive = location.pathname.includes('/profile');
  const isCatalogActive = location.pathname.includes('/catalog');
  const isAccountsActive = location.pathname.includes('/accounts') && !location.pathname.includes('/users');
  const isUsersActive = location.pathname.includes('/users');

  // Track navigation changes and store last visited route
  useEffect(() => {
    const currentPath = location.pathname;
    // Only store valid navigation routes (not login or root)
    const validRoutes = ['/bols', '/rate-calculator', '/inventory', '/orders', '/ext-orders', '/warehouse-receipts', '/invoices', '/profile', '/catalog', '/accounts', '/users', '/pending-approvals'];
    const isValidRoute = validRoutes.some(route => currentPath.includes(route));
    
    if (isValidRoute && user) {
      console.log('Storing last navigation route:', currentPath);
      sessionStorage.setItem('lastNavigationRoute', currentPath);
    }
  }, [location.pathname, user]);

  const handleMouseEnter = () => {
    // Only allow hover on desktop
    if (window.innerWidth > 768) {
      // Clear any pending collapse
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      // Expand after a short delay to prevent accidental triggers
      hoverTimeoutRef.current = setTimeout(() => {
        setIsExpanded(true);
      }, 100);
    }
  };

  const handleMouseLeave = () => {
    // Only auto-collapse on desktop
    if (window.innerWidth > 768) {
      // Clear any pending expansion
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      // Collapse after a longer delay to ensure user meant to leave
      hoverTimeoutRef.current = setTimeout(() => {
        setIsExpanded(false);
      }, 1000);
    }
  };

  // Mobile navigation handler - collapse after selection
  const handleNavClick = () => {
    // Check if we're on mobile (768px or less)
    if (window.innerWidth <= 768 && isExpanded) {
      // Collapse after navigation on mobile
      setTimeout(() => {
        setIsExpanded(false);
      }, 150);
    }
  };

  // Logo click handler - only way to toggle menu on mobile
  const handleLogoClick = () => {
    // Toggle on mobile
    if (window.innerWidth <= 768) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div className="layout-container">
      <div 
        className={`sidebar ${!isExpanded ? 'collapsed' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="logo-container" onClick={handleLogoClick}>
          <img 
            src={isExpanded ? tenantConfig.loginLogo : tenantConfig.navLogo} 
            alt={isExpanded ? "Full Logo" : "Nav Logo"} 
            className={`nav-logo ${isExpanded ? 'expanded' : 'collapsed'}`}
            onError={(e) => {
              // Fallback to appropriate default logo if tenant logo fails to load
              e.target.src = isExpanded ? '/bannernew.png' : '/globe2.png';
            }}
          />
        </div>
        
        <Nav className="flex-column main-nav">
          {hasPermission('transportation') && (
            <div className={`nav-group ${isBOLsActive || isRateCalcActive ? 'active' : ''}`}>
              <button 
                onClick={() => handleNavigation("/bols")}
                className={`nav-link-main ${isBOLsActive ? 'active' : ''}`}
                title="BOLs"
              >
                <FontAwesomeIcon icon={faTruckMoving} fixedWidth /> 
                {isExpanded && <span>BOLs</span>}
              </button>
              <button 
                onClick={() => handleNavigation("/rate-calculator")}
                className={`nav-link-sub ${isRateCalcActive ? 'active' : ''}`}
                title="Rate Calculator"
              >
                <FontAwesomeIcon icon={faCalculator} fixedWidth /> 
                {isExpanded && <span>Rate Calculator</span>}
              </button>
            </div>
          )}

          {hasPermission('inventory') && (
            <div className={`nav-group ${isInventoryActive || location.pathname.includes('/pending-approvals') ? 'active' : ''}`}>
              <button 
                onClick={() => handleNavigation("/inventory")}
                className={`nav-link-main ${isInventoryActive && !isOrdersActive && !isCatalogActive ? 'active' : ''}`}
                title="Inventory"
              >
                <FontAwesomeIcon icon={faWarehouse} fixedWidth /> 
                {isExpanded && <span>Inventory</span>}
              </button>
              <button 
                onClick={() => handleNavigation("/orders")}
                className={`nav-link-sub ${isOrdersActive ? 'active' : ''}`}
                title="Transactions"
              >
                <FontAwesomeIcon icon={faShoppingCart} fixedWidth /> 
                {isExpanded && <span>Transactions</span>}
              </button>
              {hasPermission('inventory', 'approve') && (
                <button 
                  onClick={() => handleNavigation("/pending-approvals")}
                  className={`nav-link-sub ${location.pathname.includes('/pending-approvals') ? 'active' : ''}`}
                  title="Pending Approvals"
                >
                  <FontAwesomeIcon icon={faClipboardCheck} fixedWidth /> 
                  {isExpanded && <span>Pending Approvals</span>}
                </button>
              )}
              <button 
                onClick={() => handleNavigation("/catalog")}
                className={`nav-link-sub ${isCatalogActive ? 'active' : ''}`}
                title="Catalog"
              >
                <FontAwesomeIcon icon={faBook} fixedWidth /> 
                {isExpanded && <span>Catalog</span>}
              </button>
            </div>
          )}

          {hasPermission('ext_orders') && (
            <div className={`nav-group ${location.pathname.includes('/ext-orders') ? 'active' : ''}`}>
              <button 
                onClick={() => handleNavigation("/ext-orders")}
                className={`nav-link-main ${location.pathname.includes('/ext-orders') ? 'active' : ''}`}
                title="External Orders"
              >
                <FontAwesomeIcon icon={faFileAlt} fixedWidth /> 
                {isExpanded && <span>External Orders</span>}
              </button>
            </div>
          )}

          {hasPermission('warehouse_receipts') && (
            <div className={`nav-group ${isWarehouseReceiptsActive ? 'active' : ''}`}>
              <button 
                onClick={() => handleNavigation("/warehouse-receipts")}
                className={`nav-link-main ${isWarehouseReceiptsActive ? 'active' : ''}`}
                title="Warehouse Receipts"
              >
                <FontAwesomeIcon icon={faFileInvoice} fixedWidth /> 
                {isExpanded && <span>Warehouse Receipts</span>}
              </button>
            </div>
          )}

          {hasPermission('accounts') && (
            <div className={`nav-group ${isAccountsActive ? 'active' : ''}`}>
              <button 
                onClick={() => handleNavigation("/accounts")}
                className={`nav-link-main ${isAccountsActive ? 'active' : ''}`}
                title="Accounts"
              >
                <FontAwesomeIcon icon={faUsers} fixedWidth /> 
                {isExpanded && <span>Accounts</span>}
              </button>
            </div>
          )}

          {permissions?.isAdmin && (
            <div className={`nav-group ${isUsersActive ? 'active' : ''}`}>
              <button 
                onClick={() => handleNavigation("/users")}
                className={`nav-link-main ${isUsersActive ? 'active' : ''}`}
                title="Users"
              >
                <FontAwesomeIcon icon={faUserCog} fixedWidth /> 
                {isExpanded && <span>Users</span>}
              </button>
            </div>
          )}
        </Nav>

        <Nav className="flex-column bottom-nav">
          <button 
            onClick={() => handleNavigation("/profile")}
            className={`nav-link-main ${isProfileActive ? 'active' : ''}`}
            title="Profile"
          >
            <FontAwesomeIcon icon={faUserCog} fixedWidth /> 
            {isExpanded && <span>Profile</span>}
          </button>

          <button 
            onClick={(e) => {
              handleNavClick(e);
              handleLogout();
            }} 
            className="nav-link-logout"
            title="Logout"
          >
            <FontAwesomeIcon icon={faSignOutAlt} fixedWidth />
            {isExpanded && <span>Logout</span>}
          </button>
        </Nav>
      </div>
      <main className={`main-content ${!isExpanded ? 'expanded' : ''}`}>
        {pendingApprovalsCount > 0 && hasPermission('inventory', 'approve') && (
          <div 
            className="pending-approval-banner"
            onClick={() => navigate(getTenantLink('/pending-approvals'))}
            style={{
              background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)',
              color: 'white',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              borderBottom: '3px solid #d35400',
              animation: 'pulse 2s ease-in-out infinite',
              zIndex: 1000,
              margin: 0,
              flexShrink: 0
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <FontAwesomeIcon 
                icon={faClipboardCheck} 
                size="2x"
                style={{ animation: 'bounce 1s ease-in-out infinite' }}
              />
              <div>
                <h5 style={{ margin: 0, fontWeight: 'bold', fontSize: '1.2rem' }}>
                  {pendingApprovalsCount} Order{pendingApprovalsCount !== 1 ? 's' : ''} Awaiting Approval
                </h5>
                <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.95 }}>
                  Click here to review and approve pending orders
                </p>
              </div>
            </div>
            <FontAwesomeIcon 
              icon={faClipboardCheck} 
              size="3x"
              style={{ opacity: 0.3 }}
            />
          </div>
        )}
        <div style={{ padding: '0.5rem', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export default Layout;